/**
 * Demo Preview — admin top-bar mode switcher + identity picker.
 *
 * Surface area is intentionally tiny: a pill with the current mode
 * label that opens a dialog listing every active demo identity. When
 * the admin selects one, we mint a Demo Token and reload the page so
 * AuthContext re-fetches `/auth/me` with the demo header and the
 * entire app renders as that profile.
 *
 * The component renders nothing for non-admin users and hides itself
 * if the feature flag is OFF, so it's safe to mount unconditionally
 * from `AdminTopBar`.
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDemoPreview } from "@/contexts/DemoPreviewContext";
import { useLang } from "@/i18n";
import { Eye, EyeOff } from "lucide-react";

const DemoModeSwitcher = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { featureFlag, isActive, listIdentities, startSession } = useDemoPreview();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vertical, setVertical] = useState("tennis");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listIdentities({ vertical_key: vertical })
      .then(setItems)
      .finally(() => setLoading(false));
  }, [open, vertical, listIdentities]);

  // Lock body scroll while the modal is open. Restores on close /
  // unmount so the admin shell scrolls normally again.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc to dismiss the modal — standard dialog UX.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (featureFlag.loading || !featureFlag.can_use) return null;
  if (isActive) return null; // hidden when a demo session is already running.

  const onEnter = async (id) => {
    try {
      await startSession(id);
      setOpen(false);
      // Force the rest of the app to re-evaluate auth state with the
      // demo token. A full reload to `/app` is the simplest reliable
      // path here — AuthContext.refresh() doesn't survive a token
      // swap because some pages eagerly cache the user prop.
      window.location.assign("/app");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("demo-preview start failed", e);
    }
  };

  // Modal is rendered through a portal to `document.body` so it escapes
  // the sticky `AdminTopBar` stacking context (which otherwise would
  // clip the overlay and produce the "off-positioned panel" effect
  // Roberto reported). Portal target is `document.body` — safe to use
  // at this point because the component never renders during SSR.
  const modalNode = open ? (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/50 backdrop-blur-sm"
      data-testid="demo-preview-picker-overlay"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[85vh] flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-preview-picker"
      >
        {/* Header — sticky inside the modal so it stays visible when the
            identity list scrolls. */}
        <div className="flex items-start justify-between gap-3 p-6 pb-3 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {t("demoPreview.picker.title")}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {t("demoPreview.picker.subtitle")}
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none shrink-0"
            aria-label="close"
            data-testid="demo-preview-picker-close"
          >×</button>
        </div>

        {/* Body — the only element that scrolls when content overflows. */}
        <div className="overflow-y-auto p-6 pt-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Vertical:</span>
            {["tennis"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVertical(v)}
                className={`px-2 py-1 rounded-full border ${vertical === v ? "border-purple-400 bg-purple-50 text-purple-700" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                data-testid={`demo-preview-vertical-${v}`}
              >
                {v}
              </button>
            ))}
          </div>

          {loading && (
            <div className="text-center text-slate-500 py-8 text-sm">Loading…</div>
          )}

          {!loading && items.length === 0 && (
            <div className="text-center text-slate-500 py-8 text-sm" data-testid="demo-preview-picker-empty">
              {t("demoPreview.picker.empty")}
            </div>
          )}

          {!loading && items.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch" data-testid="demo-preview-picker-list">
              {items.map((it) => {
                const roleLabel = t(`demoPreview.roles.${it.profile_type}`) || it.profile_type;
                return (
                  <li
                    key={it.id}
                    className="border border-slate-200 rounded-xl p-3 hover:border-purple-300 hover:bg-purple-50/40 transition flex flex-col"
                    data-testid={`demo-preview-card-${it.id}`}
                  >
                    {/* Top half: avatar + identity meta. `flex-1` pushes the
                        action button to the bottom of the card so every
                        card's CTA aligns at the same baseline regardless
                        of summary length (Feb 2026 cosmetic ask). */}
                    <div className="flex items-start gap-3 flex-1 min-h-0">
                      <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm shrink-0">
                        {it.avatar_initials || it.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 text-sm truncate">{it.name}</div>
                        <div className="text-xs text-purple-700 font-medium mt-0.5">{roleLabel}</div>
                        {it.summary && (
                          <div className="text-[11px] text-slate-500 mt-1 leading-snug line-clamp-2">{it.summary}</div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-wide">
                          {it.locale} · {it.country} · {it.vertical_key}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onEnter(it.id)}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold py-1.5 transition shrink-0"
                      data-testid={`demo-preview-enter-${it.id}`}
                    >
                      <EyeOff className="w-3 h-3" />
                      {t("demoPreview.picker.enter")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-100 transition"
        data-testid="demo-preview-switcher"
        title={t("demoPreview.modeSwitcher.demoPreview")}
      >
        <Eye className="w-3.5 h-3.5" />
        <span>{t("demoPreview.modeSwitcher.demoPreview")}</span>
      </button>
      {modalNode && createPortal(modalNode, document.body)}
    </>
  );
};

export default DemoModeSwitcher;
